// src/layout/builders/ProfileDetails/ProfileDetails.jsx
import { useState, useEffect, useCallback, useRef } from "react";
import useProfileStore from "../../../store/builders/useProfileStore";
import { toast } from "react-hot-toast";
import SimpleRichTextEditor from "../../../components/common/SimpleRichTextEditor";

const ProfileDetails = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const {
    profile,
    fetchProfile,
    updateProfile,
    isLoading,
    isUpdating,
  } = useProfileStore();

  const aboutContentRef = useRef(""); // Ref to track content reliably during save
  const justSavedRef = useRef(false); // Flag to prevent sync conflict after save
  const lastSavedAboutRef = useRef(""); // Track the last saved about value to prevent overwrite

  const loadProfile = async () => {
    await fetchProfile();
  };

  useEffect(() => {
    if (!profile) {
      loadProfile();
    }
  }, [profile, fetchProfile]);

  useEffect(() => {
    // Only sync when NOT editing and NOT just saved
    if (profile?.builder && !isEditing && !justSavedRef.current) {
      const backendAbout = profile.builder.about || "";
      const currentFormAbout = formData.about || "";

      // If we just saved a value and backend still has the old value, don't overwrite
      if (lastSavedAboutRef.current) {
        // If backend matches what we saved, it's safe to sync
        if (backendAbout === lastSavedAboutRef.current) {
          console.log("[ProfileDetails] Backend updated with our saved value, syncing");
          setFormData(profile.builder);
          aboutContentRef.current = backendAbout;
          // Clear the saved ref since backend is now in sync
          lastSavedAboutRef.current = "";
          return;
        }

        // If backend has old value but we have new value in formData, keep our new value
        if (backendAbout !== lastSavedAboutRef.current && currentFormAbout === lastSavedAboutRef.current) {
          console.log("[ProfileDetails] Backend still has old value, keeping our saved value:", {
            backend: backendAbout.substring(0, 50),
            saved: lastSavedAboutRef.current.substring(0, 50)
          });
          return;
        }
      }

      // Normal sync - no recent save or backend has new data
      console.log("[ProfileDetails] Syncing from profile.builder:", {
        about: backendAbout,
        about_length: backendAbout?.length || 0
      });
      setFormData(profile.builder);
      aboutContentRef.current = backendAbout;
    }
  }, [profile, isEditing]);

  const toggleEdit = useCallback(() => {
    setIsEditing((prev) => {
      const newValue = !prev;
      // When starting to edit, clear the saved ref to allow fresh edits
      if (newValue) {
        lastSavedAboutRef.current = "";
        justSavedRef.current = false;
      }
      return newValue;
    });
  }, []);

  const handleInputChange = useCallback((key, value) => {
    let processedValue = value;
    if (["contact_number", "whatsapp_number"].includes(key)) {
      processedValue = value.replace(/\D/g, "").slice(0, 10);
    } else if (key === "establishment_year") {
      processedValue = value.replace(/\D/g, "").slice(0, 4);
    }

    if (key === "about") {
      // For about field, use the raw value (no processing needed for HTML content)
      processedValue = value || "";
      // Always update the ref when about changes - this is critical for save
      aboutContentRef.current = processedValue;
      console.log("[ProfileDetails] ✅ About content updated in handleInputChange:", {
        key,
        value_length: processedValue.length,
        preview: processedValue.substring(0, 100),
        full_content: processedValue
      });
    }

    setFormData((prev) => ({
      ...prev,
      [key]: processedValue,
    }));
  }, []);

  const validateForm = () => {
    if (!formData.company_name?.trim()) {
      toast.error("Company Name is required.");
      return false;
    }
    if (!formData.engineer_name?.trim()) {
      toast.error("Engineer Name is required.");
      return false;
    }
    if (!formData.email?.trim()) {
      toast.error("Email is required.");
      return false;
    }
    if (!/\S+@\S+\.\S+/.test(formData.email)) {
      toast.error("Please enter a valid email address.");
      return false;
    }
    if (!formData.contact_number || formData.contact_number.length !== 10) {
      toast.error("Valid 10-digit Phone Number is required.");
      return false;
    }
    if (formData.establishment_year) {
      const year = parseInt(formData.establishment_year);
      const currentYear = new Date().getFullYear();
      if (isNaN(year) || year < 1900 || year > currentYear) {
        toast.error(`Establishment year must be between 1900 and ${currentYear}.`);
        return false;
      }
    } else {
      toast.error("Establishment Year is required.");
      return false;
    }
    if (!formData.gender) {
      toast.error("Gender is required.");
      return false;
    }
    // Added validation for About Company
    if (!formData.about?.trim()) {
      toast.error("About Company is required.");
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      // Get content from formData (textarea updates this directly via onChange)
      let aboutContent = formData.about || "";

      // Fallback to aboutContentRef if formData doesn't have it
      if (!aboutContent) {
        aboutContent = aboutContentRef.current || "";
      }

      console.log("[ProfileDetails] 📤 CONTENT TO SAVE:", {
        length: aboutContent.length,
        content: aboutContent,
        preview: aboutContent.substring(0, 200),
        formDataAbout: formData.about,
        aboutContentRef: aboutContentRef.current
      });

      const payload = {
        engineer_name: formData.engineer_name,
        company_name: formData.company_name,
        establishment_year: formData.establishment_year ? parseInt(formData.establishment_year) : null,
        contact_number: formData.contact_number,
        whatsapp_number: formData.whatsapp_number,
        email: formData.email,
        gender: formData.gender,
        about: aboutContent,
      };

      console.log("[ProfileDetails] Payload to send:", JSON.stringify(payload, null, 2));

      const response = await updateProfile(payload);

      // Handle both boolean and object returns for backward compatibility logic
      const isSuccess = typeof response === 'object' ? response.success : response;

      if (isSuccess) {
        // Store the saved about value to compare later
        const savedAboutValue = payload.about || "";
        lastSavedAboutRef.current = savedAboutValue;

        // Set flag to prevent useEffect from syncing stale data immediately after save
        justSavedRef.current = true;

        // Immediately update formData with saved values to prevent display revert
        setFormData(prev => ({
          ...prev,
          engineer_name: payload.engineer_name,
          company_name: payload.company_name,
          establishment_year: payload.establishment_year?.toString() || "",
          contact_number: payload.contact_number,
          whatsapp_number: payload.whatsapp_number,
          email: payload.email,
          gender: payload.gender,
          about: savedAboutValue
        }));
        aboutContentRef.current = savedAboutValue;

        console.log("[ProfileDetails] Saved successfully, new about value:", savedAboutValue.substring(0, 100));

        setIsEditing(false);

        // Wait for fetchProfile to complete, then reset the flag after a delay
        // This gives the backend time to process and return the updated value
        setTimeout(() => {
          console.log("[ProfileDetails] Resetting sync flag after save");
          justSavedRef.current = false;
        }, 2000);
      } else {
        // Log specific failure if available in response
        if (typeof response === 'object' && response.message) {
          console.error("Update failed:", response.message);
        }
      }

    } catch (error) {
      console.error("Error updating profile (handleSubmit):", error);
      toast.error(`Update failed: ${error.message || "An unexpected error occurred."}`);
    }
  };

  if (isLoading && !profile) {
    return (
      <div className="flex justify-center items-center py-6">
        <div className="w-6 h-6 border-2 border-blue-500 border-dashed rounded-full animate-spin"></div>
      </div>
    );
  }

  const fields = [
    { key: "ducktail_id", label: "Ducktail ID", editable: false },
    { key: "company_name", label: "Company Name", required: true },
    { key: "establishment_year", label: "Establishment Year", type: "number", required: true },
    { key: "contact_number", label: "Phone Number", type: "tel", required: true },
    { key: "email", label: "Email", type: "email", required: true },
    { key: "whatsapp_number", label: "WhatsApp Number", type: "tel" },
    { key: "engineer_name", label: "Engineer Name", required: true },
    {
      key: "gender",
      label: "Gender",
      type: "select",
      options: [
        { label: "-- Select --", value: "" },
        { label: "Male", value: "male" },
        { label: "Female", value: "female" },
        { label: "Other", value: "other" },
      ],
      required: true,
    },
  ];

  return (
    <div className="py-2 text-xs sm:text-sm">
      <div className="flex items-center justify-start gap-4 mb-4 border-b border-gray-400 pb-3">
        <h2 className="text-sm font-semibold">My Details</h2>
        <div className="flex items-center gap-2">
          {isEditing && (
            <button
              className="px-3 py-1.5 bg-green-500 text-white rounded-md hover:bg-green-600 text-xs transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={handleSubmit}
              disabled={isUpdating}
            >
              {isUpdating ? "Saving..." : "Save Changes"}
            </button>
          )}
          <button
            className="px-3 py-1.5 bg-blue-500 text-white rounded-md hover:bg-blue-600 text-xs transition-colors"
            onClick={toggleEdit}
            disabled={isUpdating}
          >
            {isEditing ? (isUpdating ? "Saving..." : "Cancel") : "Edit"}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
        {fields.map(({ key, label, editable = true, type = "text", options = [], required = false }) => {
          const isPhoneField = key === "contact_number" || key === "whatsapp_number";
          return (
            <div key={key} className="space-y-1.5">
              <label className={`block text-xs sm:text-sm font-medium ${isEditing && required && !formData[key] ? "text-red-500" : "text-gray-600"}`}>
                {label} {required && isEditing && <span className="text-red-500">*</span>}
              </label>
              {type === "select" ? (
                <select
                  value={formData[key] || ""}
                  onChange={(e) => handleInputChange(key, e.target.value)}
                  disabled={!editable || !isEditing || isUpdating}
                  className={`w-full p-2 sm:p-2.5 rounded-md text-xs sm:text-sm transition-colors outline-none ${!editable || !isEditing
                    ? "border-transparent bg-gray-50 text-gray-600 cursor-default"
                    : isEditing && required && !formData[key]
                      ? "border border-red-500 bg-white"
                      : "border border-gray-300 bg-white focus:border-blue-500 focus:ring-1 focus:ring-blue-100"
                    }`}
                >
                  {options.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              ) : (
                <div className="relative">
                  {isEditing && isPhoneField && (
                    <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-2 sm:pl-3 text-xs sm:text-sm text-gray-500">
                      +91
                    </span>
                  )}
                  <input
                    type={type === "number" && isEditing ? "number" : "text"}
                    inputMode={isPhoneField || key === "establishment_year" ? "numeric" : "text"}
                    maxLength={isPhoneField ? 10 : key === "establishment_year" ? 4 : undefined}
                    value={!isEditing && isPhoneField && formData[key] ? `+91 ${formData[key]}` : (formData[key] || "")}
                    onChange={(e) => handleInputChange(key, e.target.value)}
                    disabled={!editable || !isEditing || isUpdating}
                    className={`w-full p-2 sm:p-2.5 rounded-md text-xs sm:text-sm transition-colors outline-none ${isEditing && isPhoneField ? "pl-8 sm:pl-9" : ""
                      } ${!editable
                        ? "bg-gray-100 text-gray-600 cursor-not-allowed border-transparent"
                        : isEditing
                          ? required && !formData[key]
                            ? "border border-red-500 bg-white focus:ring-1 focus:ring-red-100"
                            : "border border-gray-300 bg-white focus:border-blue-500 focus:ring-1 focus:ring-blue-100"
                          : !formData[key] && required
                            ? "border border-red-500 bg-red-50 text-red-600 cursor-default"
                            : "border-transparent bg-gray-50 text-gray-800 cursor-default"
                      }`}
                    placeholder={isEditing ? `Enter ${label.toLowerCase()}` : ""}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-6 sm:mt-8 space-y-2">
        <label className={`block text-xs sm:text-sm font-medium ${!formData.about?.trim() ? "text-red-500" : "text-gray-700"}`}>
          About Company {isEditing && <span className="text-red-500">*</span>}
        </label>
        {isEditing ? (
          <div className={`${!formData.about?.trim() ? "border-2 border-red-500 rounded-lg" : ""}`}>
            <SimpleRichTextEditor
              value={formData.about || ""}
              onChange={(content) => {
                handleInputChange("about", content);
              }}
              placeholder="Tell us about your company... Use the toolbar to format text (bold, italic, underline, bullet points)"
              disabled={isUpdating}
              minHeight="200px"
            />
          </div>
        ) : (
          <div
            className={`w-full p-3 sm:p-4 rounded-lg text-xs sm:text-sm border min-h-[100px] sm:min-h-[120px] overflow-auto ${!formData.about?.trim()
              ? "border-red-500 bg-red-50 text-red-600"
              : "border-gray-200 bg-gray-50/50 text-gray-700"
              }`}
            dangerouslySetInnerHTML={{ __html: formData.about || aboutContentRef.current || '<p class="font-medium text-red-600">No description provided - Required</p>' }}
          />
        )}
      </div>
    </div>
  );
};

export default ProfileDetails;